import type {
  CreateDownloadUrlResponse,
  CreateMediaRequest,
  CreateMediaResponse,
  CreateUploadUrlRequest,
  CreateUploadUrlResponse,
  GetAllMediaParams,
  GetAllMediaResponse,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { trpcClient } from 'trpc';

export enum MediaEndpoint {
  Base = '',
  CreateUploadUrl = 'create-upload-url',
  CreateDownloadUrl = 'download-url',
}

export class MediaApi {
  private readonly aggregateRoot = 'api/aggregate/patient';

  private createPath(patientId: string, endpoint: string, resourceId?: string) {
    return compact([
      this.aggregateRoot,
      patientId,
      'media',
      resourceId,
      endpoint,
    ]).join('/');
  }

  /**
   * Get an SWR Key associated with the `getMedia` function.
   */
  public getMediaSWRKey = (
    patientId: string,
    ...endpoint: MediaEndpoint[]
  ): string => this.createPath(patientId, endpoint.join('_'));

  public createUploadUrl = async (
    patientId: string,
    body: CreateUploadUrlRequest,
  ): Promise<CreateUploadUrlResponse> => {
    const result = await trpcClient.media.createUploadURL.mutate({
      contentType: body.contentType,
      patientId,
      title: body.title,
    });

    return result;
  };

  public createDownloadUrl = async (
    _patientId: string,
    mediaId: string,
  ): Promise<CreateDownloadUrlResponse> => {
    const url = await trpcClient.media.getDownloadURL.query({ id: mediaId });

    return { url };
  };

  public createMedia = async (
    patientId: string,
    body: CreateMediaRequest,
  ): Promise<CreateMediaResponse> => {
    const media = await trpcClient.media.create.mutate({
      patient: patientId,
      media: body.media,
    });

    return {
      media: media,
    };
  };

  public getMedia = async (
    patientId: string,
    params: GetAllMediaParams,
  ): Promise<GetAllMediaResponse> => {
    return await trpcClient.media.getAll.query({
      subject: patientId,
      ...params,
    });
  };
}
